import { withAlpha, useAccentFn } from "@/lib/color";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import TeamLogo from "@/components/TeamLogo";

const COLUMNS = [
    { key: "name", label: "Squadra", align: "left", numeric: false, width: "minmax(150px, 1fr)" },
    { key: "manager_name", label: "Mister", align: "left", numeric: false, width: "minmax(110px, 0.7fr)" },
    { key: "division", label: "Div", align: "left", numeric: false, width: "56px" },
    { key: "vg11", label: "VG11", align: "right", numeric: true, width: "76px" },
    { key: "main_count", label: "Rosa", align: "right", numeric: true, width: "56px" },
    { key: "ugly_count", label: "Ugly", align: "right", numeric: true, width: "64px" },
    { key: "loans_out_season", label: "Prestiti", align: "right", numeric: true, width: "72px" },
    { key: "nfc_count", label: "NFC", align: "right", numeric: true, width: "64px" },
    { key: "svi_count", label: "SVI", align: "right", numeric: true, width: "64px" },
    { key: "budget", label: "Budget", align: "right", numeric: true, width: "104px" },
    { key: "applied_wages_total", label: "Ingaggi", align: "right", numeric: true, width: "104px" },
    { key: "team_value", label: "Valore TM", align: "right", numeric: true, width: "104px" },
];

export default function Squadre() {
    const acc = useAccentFn();
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [params, setParams] = useSearchParams();

    const sortKey = params.get("sort") || "vg11";
    const sortDir = params.get("dir") || "desc"; // "asc" | "desc"
    const divFilter = params.get("div") || "";
    const q = params.get("q") || "";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/teams/standings");
                if (!cancelled) setTeams(data || []);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const divisions = useMemo(() => {
        const map = new Map();
        for (const t of teams) {
            if (t.division?.code) {
                map.set(t.division.code, t.division);
            }
        }
        return Array.from(map.values()).sort((a, b) => (a.level || 0) - (b.level || 0));
    }, [teams]);

    const visible = useMemo(() => {
        const term = q.trim().toLowerCase();
        let filtered = divFilter
            ? teams.filter((t) => t.division?.code === divFilter)
            : teams.slice();
        if (term) {
            filtered = filtered.filter(
                (t) =>
                    (t.name || "").toLowerCase().includes(term) ||
                    (t.manager_name || "").toLowerCase().includes(term)
            );
        }

        const dir = sortDir === "asc" ? 1 : -1;
        filtered.sort((a, b) => {
            const col = COLUMNS.find((c) => c.key === sortKey);
            let av; let bv;
            if (sortKey === "division") {
                av = a.division?.level ?? 999;
                bv = b.division?.level ?? 999;
            } else if (sortKey === "name" || sortKey === "manager_name") {
                av = (a[sortKey] || "").toLowerCase();
                bv = (b[sortKey] || "").toLowerCase();
            } else {
                av = a[sortKey];
                bv = b[sortKey];
            }
            // Push null/undefined to the bottom regardless of direction
            const aNull = av == null || av === "";
            const bNull = bv == null || bv === "";
            if (aNull && bNull) return 0;
            if (aNull) return 1;
            if (bNull) return -1;
            if (col?.numeric) return (av - bv) * dir;
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return filtered;
    }, [teams, sortKey, sortDir, divFilter, q]);

    const handleSort = (key) => {
        const next = new URLSearchParams(params);
        if (sortKey === key) {
            next.set("dir", sortDir === "asc" ? "desc" : "asc");
        } else {
            next.set("sort", key);
            // default direction: desc for numeric, asc for text
            const col = COLUMNS.find((c) => c.key === key);
            next.set("dir", col?.numeric || key === "division" ? "desc" : "asc");
        }
        setParams(next, { replace: true });
    };

    const setDivFilter = (code) => {
        const next = new URLSearchParams(params);
        if (code) next.set("div", code);
        else next.delete("div");
        setParams(next, { replace: true });
    };

    const setSearch = (val) => {
        const next = new URLSearchParams(params);
        if (val) next.set("q", val);
        else next.delete("q");
        setParams(next, { replace: true });
    };

    const gridTemplate = COLUMNS.map((c) => c.width).join(" ");

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="squadre-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <h1 className="op-heading text-5xl mb-3"><span className="op-gradient-text">SQUADRE</span></h1>
                    <p className="text-t8f95a3 max-w-2xl text-sm leading-relaxed">
                        {teams.length} club totali. Clicca su un'intestazione per ordinare, sul nome
                        per vedere rosa, stipendi e budget.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca squadra o Mister…"
                        className="op-input op-mono text-xs py-1.5 min-w-[200px]"
                        data-testid="squadre-search-input"
                    />
                    <button
                        onClick={() => setDivFilter("")}
                        className={`op-mono text-[11px] px-3 py-1.5 border transition-colors ${
                            !divFilter
                                ? "bg-tffffff text-black border-tffffff"
                                : "border-t22252a text-t8f95a3 hover:text-tffffff"
                        }`}
                        data-testid="filter-all"
                    >
                        TUTTE
                    </button>
                    {divisions.map((d) => {
                        const active = divFilter === d.code;
                        return (
                            <button
                                key={d.code}
                                onClick={() => setDivFilter(d.code)}
                                className="op-mono text-[11px] px-3 py-1.5 border transition-colors"
                                style={{
                                    background: active ? acc(d.accent_color) : "transparent",
                                    color: active ? "rgb(var(--tc-000000))" : acc(d.accent_color),
                                    borderColor: withAlpha(acc(d.accent_color), "88"),
                                }}
                                data-testid={`filter-div-${d.code}`}
                            >
                                {d.code}
                            </button>
                        );
                    })}
                </div>
            </div>

            {err && (
                <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-sm p-3 mb-6" data-testid="squadre-error">
                    {err}
                </div>
            )}

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="op-card overflow-x-auto hidden md:block" data-testid="squadre-table">
                        {/* Header row */}
                        <div
                            className="grid items-center px-3 py-3 border-b-2 border-t22252a op-mono text-[10px] uppercase text-t8f95a3"
                            style={{ gridTemplateColumns: gridTemplate, gap: "0.4rem" }}
                        >
                            {COLUMNS.map((c) => {
                                const active = sortKey === c.key;
                                return (
                                    <button
                                        key={c.key}
                                        onClick={() => handleSort(c.key)}
                                        className={`text-${c.align} flex items-center gap-1 hover:text-tffffff transition-colors ${active ? "text-tffffff" : ""}`}
                                        style={{ justifyContent: c.align === "right" ? "flex-end" : "flex-start" }}
                                        data-testid={`sort-${c.key}`}
                                    >
                                        <span>{c.label}</span>
                                        <span className="op-mono text-[9px] w-2 inline-block">
                                            {active ? (sortDir === "asc" ? "▲" : "▼") : ""}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {visible.map((t) => (
                            <Link
                                key={t.id}
                                to={`/squadre/${t.id}`}
                                className="grid items-center px-3 py-2.5 border-b border-t1a1d24 hover:bg-t1a1d24/60 transition-colors group"
                                style={{ gridTemplateColumns: gridTemplate, gap: "0.4rem" }}
                                data-testid={`team-row-${t.id}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <TeamLogo team={t} accentColor={acc(t.division?.accent_color || "rgb(var(--tc-ffffff))")} size={32} />
                                    <div className="min-w-0">
                                        <div className="text-tffffff truncate group-hover:text-t2bb6ff transition-colors">
                                            {t.name}
                                        </div>
                                    </div>
                                </div>
                                <div className="op-mono text-xs text-tc9ccd3 truncate" data-testid={`team-manager-${t.id}`}>
                                    {t.manager_name || "—"}
                                </div>
                                <div>
                                    {t.division ? (
                                        <span
                                            className="op-mono text-[10px] uppercase px-1.5 py-0.5 border"
                                            style={{ color: acc(t.division.accent_color), borderColor: withAlpha(acc(t.division.accent_color), "66") }}
                                        >
                                            {t.division.code}
                                        </span>
                                    ) : <span className="op-mono text-[10px] text-t5c6370">—</span>}
                                </div>
                                <div className="text-right op-heading text-2xl" style={{ color: vg11Color(t.vg11) }}>
                                    {t.vg11 != null ? t.vg11.toFixed(1) : "—"}
                                </div>
                                <div className={`text-right op-mono ${t.roster_over_limit ? "text-tff3b30" : "text-tffffff"}`}>
                                    {t.main_count}
                                </div>
                                <div className={`text-right op-mono ${(t.ugly_count || 0) >= (t.ugly_capacity ?? 5) ? "text-tff3b30" : "text-tff6b00"}`} title="Ugly in rosa / slot disponibili (stadio)">
                                    {(t.ugly_count || 0)}/{t.ugly_capacity ?? 5}
                                </div>
                                <div className={`text-right op-mono ${(t.loans_out_season || 0) >= (t.max_loans_out || 6) ? "text-tff3b30" : "text-t8f95a3"}`} title="Prestiti dati in stagione (limite 6)">
                                    {(t.loans_out_season || 0)}/{t.max_loans_out || 6}
                                </div>
                                <div className={`text-right op-mono ${(t.nfc_count || 0) > (t.max_nfc_per_market || 20) ? "text-tff3b30" : "text-t2bb6ff"}`} title="Facce nuove prese nel mercato">
                                    {(t.nfc_count || 0)}/{t.max_nfc_per_market || 20}
                                </div>
                                <div className={`text-right op-mono ${(t.svi_count || 0) > (t.max_svi_per_market || 10) ? "text-tff3b30" : "text-t9d7cff"}`} title="Svincolati presi nel mercato">
                                    {(t.svi_count || 0)}/{t.max_svi_per_market || 10}
                                </div>
                                <div className="text-right op-mono text-t2bb6ff">{formatMln(t.budget)}</div>
                                <div className="text-right op-mono text-te8ff00">{formatMln(t.applied_wages_total)}</div>
                                <div className="text-right op-mono text-t7ce38b">{formatMln(t.team_value)}</div>
                            </Link>
                        ))}
                    </div>

                    {/* Mobile cards */}
                    <div className="md:hidden space-y-2" data-testid="squadre-mobile-list">
                        <div className="flex gap-2 overflow-x-auto pb-2 op-mono text-[10px] uppercase text-t8f95a3">
                            {[
                                { k: "vg11", l: "VG11" },
                                { k: "budget", l: "Budget" },
                                { k: "applied_wages_total", l: "Ingaggi" },
                                { k: "team_value", l: "Valore TM" },
                                { k: "name", l: "Nome" },
                                { k: "manager_name", l: "Mister" },
                                { k: "main_count", l: "Rosa" },
                            ].map((c) => (
                                <button
                                    key={c.k}
                                    onClick={() => handleSort(c.k)}
                                    className={`px-2 py-1 border whitespace-nowrap ${sortKey === c.k ? "text-tffffff border-tffffff" : "border-t22252a"}`}
                                >
                                    {c.l} {sortKey === c.k ? (sortDir === "asc" ? "▲" : "▼") : ""}
                                </button>
                            ))}
                        </div>
                        {visible.map((t) => (
                            <Link
                                key={t.id}
                                to={`/squadre/${t.id}`}
                                className="op-card p-3 flex items-center gap-3"
                            >
                                <TeamLogo team={t} accentColor={acc(t.division?.accent_color || "rgb(var(--tc-ffffff))")} size={40} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-tffffff truncate">{t.name}</span>
                                        {t.division && (
                                            <span
                                                className="op-mono text-[9px] uppercase px-1 py-0.5 border"
                                                style={{ color: acc(t.division.accent_color), borderColor: withAlpha(acc(t.division.accent_color), "66") }}
                                            >
                                                {t.division.code}
                                            </span>
                                        )}
                                    </div>
                                    <div className="op-mono text-[10px] text-t8f95a3 truncate mt-0.5">
                                        {t.manager_name || "—"}
                                    </div>
                                    <div className="flex flex-wrap gap-2 op-mono text-[10px] text-t8f95a3 mt-1">
                                        <span>VG11 <span className="text-tffffff">{t.vg11 != null ? t.vg11.toFixed(1) : "—"}</span></span>
                                        <span>Rosa <span className="text-tffffff">{t.main_count}/25</span></span>
                                        <span>Ugly <span className={(t.ugly_count || 0) >= (t.ugly_capacity ?? 5) ? "text-tff3b30" : "text-tff6b00"}>{(t.ugly_count || 0)}/{t.ugly_capacity ?? 5}</span></span>
                                        <span>{t.nfc_label || "NFC"} <span className="text-t2bb6ff">{(t.nfc_count || 0)}/{t.max_nfc_per_market || 20}</span></span>
                                        <span>{t.svi_label || "SVI"} <span className="text-t9d7cff">{(t.svi_count || 0)}/{t.max_svi_per_market || 10}</span></span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 op-mono text-[10px] text-t8f95a3 mt-0.5">
                                        <span>Budget <span className="text-t2bb6ff">{formatMln(t.budget)}</span></span>
                                        <span>Ingaggi <span className="text-te8ff00">{formatMln(t.applied_wages_total)}</span></span>
                                        <span>Valore TM <span className="text-t7ce38b">{formatMln(t.team_value)}</span></span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function formatMln(v) {
    if (v == null) return "—";
    if (v === 0) return "0";
    const n = v / 1_000_000;
    if (n >= 100) return `${n.toFixed(0)}mln`;
    return `${n.toFixed(1)}mln`;
}

function vg11Color(vg) {
    if (vg == null) return "rgb(var(--tc-8f95a3))";
    if (vg >= 84) return "rgb(var(--tc-ff6b00))";
    if (vg >= 82) return "rgb(var(--tc-e8ff00))";
    if (vg >= 80) return "rgb(var(--tc-2bb6ff))";
    return "rgb(var(--tc-c9ccd3))";
}
